/**
 * @module PromotionService
 * @remarks RTK Query service for customer revenue data
 */

import { Promotions } from "@models/promotions.model";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { userApi } from "@store/api-helpers/userApi";

/**
 * API service for managing customer revenue data
 */
export const PromotionApi = createApi({
  reducerPath: "promotionApi",
  baseQuery: fetchBaseQuery({
    baseUrl: "/api/",
    prepareHeaders: (headers) => {
      const token = localStorage.getItem("auth_token");
      if (token) {
        headers.set("Authorization", token);
      }
      return headers;
    },
  }),
  tagTypes: ["Promotion"],
  endpoints: (builder) => ({
    getPromotions: builder.query<Promotions[], string>({
      async queryFn(customerNumber, { dispatch }) {
        try {
          // Use the userApi to get detailed promotion data
          const promotionDetailsResult = await dispatch(
            userApi.endpoints.getPromotionDetails.initiate(customerNumber)
          );
          
          if (promotionDetailsResult.error) {
            return { error: promotionDetailsResult.error as FetchBaseQueryError };
          }
          
          const promotionDetails = promotionDetailsResult.data;
          if (!promotionDetails || promotionDetails.length === 0) {
            return { data: [] };
          }
          
          // Transform the promotion details to match the expected format
          const formattedPromotions: Promotions[] = promotionDetails.map((detail: any) => {
            // Handle both array format and single object format
            const promotionData = Array.isArray(detail) ? detail[0] : detail;
            
            return {
              id: promotionData.id || '',
              md: promotionData.md || 0,
              promotions: promotionData.promotions || promotionData
            };
          });
          
          return { data: formattedPromotions };
        } catch (error) {
          return { 
            error: { 
              status: 'CUSTOM_ERROR', 
              data: String(error),
              error: String(error)
            } as FetchBaseQueryError 
          };
        }
      },
      providesTags: ["Promotion"],
      keepUnusedDataFor: 300,
    }),
    invalidatePromotions: builder.mutation<void, void>({
      queryFn: () => ({ data: undefined }),
      invalidatesTags: ["Promotion"],
    }),
  }),
});

// Export hooks für Verwendung in Komponenten
export const {
  useGetPromotionsQuery,
  useInvalidatePromotionsMutation,
} = PromotionApi;
